import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from '@thinkalpha/ui-core/utils';
import { useEffect, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useMyAccessList } from 'src/admin/users/user-page/hooks';
import { Label, Checkbox } from 'src/components/ui';
import { useIsSuperuserEnabled } from 'src/hooks/useIsSuperuserEnabled';
import { useNavigate } from 'src/hooks/useNavigate';

export const generateFirmNameToDisplay = (firm: { name?: string; displayName?: string }) => {
    const name = firm.name ?? '';
    if (firm.displayName && firm.displayName.length > 0 && firm.displayName !== name) {
        return `${firm.displayName} (${name})`;
    }
    return name;
};

export const useEnsureUserHasFirmAccess = (firmId: string | undefined) => {
    const navigate = useNavigate();

    useEffect(() => {
        if (!firmId) {
            navigate('/admin/firms');
            return;
        }
    }, [firmId, navigate]);

    const { data: myUserAccessList } = useMyAccessList();

    const hasAccess = myUserAccessList?.find((access) => access.id === firmId) !== undefined;
    const isFirmOwner =
        myUserAccessList?.find((access) => access.id === firmId && access.permission === 'owner') !== undefined;
    const isFirmAdmin =
        myUserAccessList?.find((access) => access.id === firmId && access.permission === 'admin') !== undefined;
    const isSuperuserEnabled = useIsSuperuserEnabled();

    useEffect(() => {
        const hasViewerPermissions = hasAccess || isSuperuserEnabled;
        if (!hasViewerPermissions && myUserAccessList !== undefined) {
            navigate('/admin/firms');
            return;
        }
    }, [myUserAccessList, hasAccess, isSuperuserEnabled, navigate]);

    return {
        isFirmOwner,
        isFirmAdmin,
        isSuperuserEnabled,
    };
};

export interface EnsureUserHasGroupAccessOptions {
    /** Grants viewer access without a direct group access entry, e.g. for admins/owners of the group's firm. */
    hasFirmAccess?: boolean;
    fallbackURL?: string;
}

export const useEnsureUserHasGroupAccess = (
    groupId: string | undefined,
    { hasFirmAccess = false, fallbackURL = '/admin/groups' }: EnsureUserHasGroupAccessOptions = {},
) => {
    const navigate = useNavigate();

    useEffect(() => {
        if (!groupId) {
            navigate(fallbackURL);
            return;
        }
    }, [groupId, navigate, fallbackURL]);

    const { data: myUserAccessList } = useMyAccessList();

    const hasAccess = myUserAccessList?.find((access) => access.id === groupId) !== undefined;
    const isGroupOwner =
        myUserAccessList?.find((access) => access.id === groupId && access.permission === 'owner') !== undefined;
    const isGroupAdmin =
        myUserAccessList?.find((access) => access.id === groupId && access.permission === 'admin') !== undefined;
    const isSuperuserEnabled = useIsSuperuserEnabled();

    useEffect(() => {
        const hasViewerPermissions = hasAccess || hasFirmAccess || isSuperuserEnabled;
        if (!hasViewerPermissions && myUserAccessList !== undefined) {
            navigate(fallbackURL);
            return;
        }
    }, [myUserAccessList, hasAccess, hasFirmAccess, isSuperuserEnabled, navigate, fallbackURL]);

    return {
        isGroupOwner,
        isGroupAdmin,
        isSuperuserEnabled,
    };
};

export interface NestedRoles {
    [key: string]: NestedRoles;
}

export const roleIsEnabled = (role: string, roles: string[]) => roles.includes(role);

export const getSubRoles = (role: string, roles: string[]) =>
    roles.filter((r) => r.substring(0, 2 + role.length) === `${role}::`);

export const anySubRoleIsEnabled = (role: string, roles: string[]): boolean => {
    const subRoles = getSubRoles(role, roles);
    return subRoles.length === 0 ? roleIsEnabled(role, roles) : subRoles.every((r) => anySubRoleIsEnabled(r, roles));
};

export const allSubRolesAreEnabled = (role: string, roles: string[]): boolean => {
    const subRoles = getSubRoles(role, roles);
    return subRoles.length === 0 ? roleIsEnabled(role, roles) : subRoles.every((r) => allSubRolesAreEnabled(r, roles));
};

export const toggleRole = (
    role: string,
    existingRoles: string[],
    allRoles: string[],
    callback: (newRoles: string[]) => void,
): void => {
    let newRoles = [...existingRoles];
    const subRoles = getSubRoles(role, allRoles);

    if (roleIsEnabled(role, existingRoles) || allSubRolesAreEnabled(role, existingRoles)) {
        /**
         *  Always remove any sub roles
         */
        newRoles = newRoles.filter((r) => !subRoles.includes(r));

        /**
         * If it doesn't have sub roles it is last in the tree branch
         * so remove the role itself too
         */
        if (!subRoles.length) {
            newRoles = newRoles.filter((r) => r !== role);
        }
    } else {
        /**
         * If it has sub roles add them all but not the role itself as it is a parent only,
         * otherwise it is last in the tree branch, add the role
         */
        if (subRoles.length) {
            newRoles = newRoles.concat(subRoles);
        } else {
            newRoles.push(role);
        }
    }

    callback(newRoles);
};

/**
 * Create the object hierarchy as the roles may not be returned
 * in the proper order and this could render the elements in wrong places
 */
export const createRolesHierarchy = (roles: string[]) => {
    const hierarchy: NestedRoles = {};

    roles.forEach((role) => {
        const split = role.split('::');
        let currentLevel = hierarchy;

        split.forEach((part, index) => {
            if (!currentLevel[part]) {
                currentLevel[part] = {};
            }

            if (index === split.length - 1) {
                // Ensure the final part is added as an empty object if it's a leaf node
                currentLevel[part] = currentLevel[part] || {};
            }

            currentLevel = currentLevel[part];
        });
    });

    return hierarchy;
};

export interface RolesSectionsIsCollapsedMap {
    [rolePath: string]: boolean;
}

export interface RolesElementsHierarchyOptions {
    id: string;
    roles: NestedRoles;
    onToggleRole: (role: string) => () => void;
    getIsVisible: (role: string) => boolean;
    getIsDisabled: (role: string) => boolean;
    getIsChecked: (role: string) => boolean;
    getIsAnySubRoleAvailable: (role: string) => boolean;
    sectionsIsCollapsedMap: RolesSectionsIsCollapsedMap;
    sectionsIsCollapsedOnChange: (isCollapsed: boolean, role: string) => void;
}

export const CreateRolesElementsFromHierarchy = ({
    options,
    path = '',
    firstIteration = true,
}: {
    options: RolesElementsHierarchyOptions;
    path?: string;
    firstIteration?: boolean;
}) => {
    const [openDropdownMap, setOpenDropdownMap] = useState<{ [role: string]: boolean }>({});

    return Object.keys(options.roles).map((role, i) => {
        return firstIteration ? (
            <RoleDropdown
                key={`${role}-${i}-${path}`}
                role={role}
                path={path}
                options={options}
                openDropdownMap={openDropdownMap}
                setOpenDropdownMap={setOpenDropdownMap}
            />
        ) : (
            <Role key={`${role}-${i}-${path}`} role={role} path={path} options={options} />
        );
    });
};

const RoleDropdown = ({
    role,
    path,
    options,
    openDropdownMap,
    setOpenDropdownMap,
}: {
    role: string | number;
    path: string;
    options: RolesElementsHierarchyOptions;
    openDropdownMap: { [role: string]: boolean };
    setOpenDropdownMap: Dispatch<SetStateAction<{ [role: string]: boolean }>>;
}) => {
    const roleString = String(role);
    const isFirstIteration = path === '';
    const currentRolePath = [path, roleString].filter((part) => part.length > 0).join('::');
    const currentRoleId = currentRolePath.replaceAll('::', '_');
    const isAnySubRoleAvailable = options.getIsAnySubRoleAvailable(currentRolePath);
    const isVisible = options.getIsVisible(currentRolePath);
    const isDisabled = options.getIsDisabled(currentRolePath);
    const isChecked = options.getIsChecked(currentRolePath);
    let name = roleString;
    const { t } = useTranslation();
    {
        // TODO: Type the key for this better
        let translated;
        if (currentRolePath.split('::').includes('widgets')) {
            translated = t((currentRolePath + '::name') as any, roleString, { returnObjects: true });
        } else {
            translated = t(('roles::' + currentRolePath) as any, roleString, { returnObjects: true });
        }
        if (typeof translated === 'string') {
            name = translated;
        }
    }
    if (!isAnySubRoleAvailable) {
        return;
    }
    return (
        <div key={currentRolePath} className="flex basis-1/6 flex-col gap-2">
            <div
                onClick={() =>
                    setOpenDropdownMap((prev) => {
                        return prev[role.toString()]
                            ? { ...prev, [role.toString()]: false }
                            : {
                                  ...prev,
                                  [role.toString()]: true,
                              };
                    })
                }
                className="flex cursor-pointer items-center justify-between gap-2 rounded-md border border-gray-700 p-4 transition-colors hover:bg-gray-800"
            >
                <div className="text-md font-semibold capitalize">{currentRolePath}</div>
                <FontAwesomeIcon
                    icon={faChevronDown}
                    className={cn('transition-transform', openDropdownMap[role.toString()] ? 'rotate-180' : 'rotate-0')}
                />
            </div>

            {role.toString() in openDropdownMap && openDropdownMap[role.toString()] && (
                <div className={cn(['space-y-0.5 pl-8', isFirstIteration && 'rounded border border-gray-700 p-2'])}>
                    {isVisible && (
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id={`${options.id}_${currentRoleId}`}
                                disabled={isDisabled}
                                checked={isChecked}
                                onCheckedChange={options.onToggleRole(currentRolePath)}
                                className={cn('peer', isDisabled ? 'cursor-not-allowed' : 'cursor-pointer')}
                            />
                            <Label
                                htmlFor={`${options.id}_${currentRoleId}`}
                                className={cn(
                                    'whitespace-nowrap',
                                    isDisabled ? 'cursor-not-allowed text-gray-400' : 'cursor-pointer',
                                )}
                            >
                                {name}
                            </Label>
                        </div>
                    )}
                    {Object.keys(options.roles[role]).length > 0 && (
                        <CreateRolesElementsFromHierarchy
                            options={{ ...options, roles: options.roles[role] }}
                            path={currentRolePath}
                            firstIteration={false}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

const Role = ({
    role,
    options,
    path,
}: {
    role: string | number;
    path: string;
    options: RolesElementsHierarchyOptions;
}) => {
    const roleString = role.toString();
    const currentRolePath = [path, roleString].filter((part) => part.length > 0).join('::');
    const currentRoleId = currentRolePath.replaceAll('::', '_');
    const isDisabled = options.getIsDisabled(currentRolePath);
    const isChecked = options.getIsChecked(currentRolePath);
    let name = roleString;
    const { t } = useTranslation();
    {
        // TODO: Type the key for this better
        let translated;
        if (currentRolePath.split('::').includes('widgets')) {
            translated = t((currentRolePath + '::name') as any, roleString, { returnObjects: true });
        } else {
            translated = t(('roles::' + currentRolePath) as any, roleString, { returnObjects: true });
        }
        if (typeof translated === 'string') {
            name = translated;
        }
    }
    return (
        <div className={cn(['space-y-0.5 pl-8'])}>
            <div className="flex items-center gap-2">
                <Checkbox
                    id={`${options.id}_${currentRoleId}`}
                    disabled={isDisabled}
                    checked={isChecked}
                    onCheckedChange={options.onToggleRole(currentRolePath)}
                    className={cn('peer', isDisabled ? 'cursor-not-allowed' : 'cursor-pointer')}
                />
                <Label
                    htmlFor={`${options.id}_${currentRoleId}`}
                    className={cn(
                        'whitespace-nowrap',
                        isDisabled ? 'cursor-not-allowed text-gray-400' : 'cursor-pointer',
                    )}
                >
                    {name}
                </Label>
            </div>
            {Object.keys(options.roles[role]).length > 0 && (
                <CreateRolesElementsFromHierarchy
                    options={{ ...options, roles: options.roles[role] }}
                    path={currentRolePath}
                    firstIteration={false}
                />
            )}
        </div>
    );
};
